import React, { useEffect, useRef } from "react";
import CTASection from "../components/CTASection";
import PageHero from "../components/PageHero";
import { SITE_ASSETS } from "../mock";
import { useSettings } from "../context/SettingsContext";
import * as Lucide from "lucide-react";
import { ArrowUpRight } from "lucide-react";

function Icon({ name, ...rest }) {
  const Cmp = Lucide[name] || Lucide.Sparkles;
  return <Cmp {...rest} />;
}

function useStaggerReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const items = Array.from(root.querySelectorAll("[data-reveal]"));
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add("in-view");
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.15 });
    items.forEach((it) => obs.observe(it));
    return () => obs.disconnect();
  }, []);
  return ref;
}

export default function ServicesPage() {
  const { services } = useSettings();
  const gridRef = useStaggerReveal();
  const stepsRef = useStaggerReveal();
  return (
    <>
      <PageHero
        eyebrow="What We Do"
        title="A complete development studio."
        italicWord="development"
        description="From land acquisition to architectural design, interiors and post-handover concierge — every Tamra service is delivered in-house with the same standards."
        image={SITE_ASSETS.hero.building}
      />

      <section className="py-24 lg:py-32 bg-[#F5EFE6] relative">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="section-eyebrow mb-4">Our Practice</div>
            <h2 className="section-title font-display">Six pillars of <span className="italic text-[#B8893C]">Tamra craft</span>.</h2>
          </div>
          <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7">
            {services.map((s, i) => (
              <div
                key={s.id}
                data-reveal
                style={{ transitionDelay: `${i * 80}ms` }}
                className="reveal service-card group bg-white border border-[#EBE0CE] p-8 lg:p-10 relative overflow-hidden cursor-pointer"
              >
                {/* Animated gold corner */}
                <div className="absolute top-0 right-0 w-0 h-0.5 bg-[#B8893C] group-hover:w-full transition-all duration-700"></div>
                <div className="absolute top-0 right-0 h-0 w-0.5 bg-[#B8893C] group-hover:h-full transition-all duration-700 delay-150"></div>
                {/* Decorative bg circle */}
                <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-[#EBE0CE]/50 group-hover:bg-[#B8893C]/15 group-hover:scale-125 transition-all duration-700"></div>

                <div className="relative">
                  <div className="text-[11px] tracking-[0.4em] text-[#B8893C] mb-5 transition-all group-hover:tracking-[0.55em]">0{i + 1}</div>
                  <div className="w-16 h-16 rounded-full border border-[#B8893C]/40 flex items-center justify-center text-[#5C2018] mb-7 transition-all duration-500 group-hover:bg-[#5C2018] group-hover:text-[#F5EFE6] group-hover:border-[#5C2018] group-hover:rotate-[8deg] group-hover:-translate-y-1 group-hover:shadow-lg">
                    <Icon name={s.icon} size={24} className="transition-transform duration-500 group-hover:scale-110" />
                  </div>
                  <div className="font-display text-2xl tracking-[0.05em] text-[#1F1B17] mb-3 transition-colors group-hover:text-[#5C2018]">{s.title}</div>
                  <p className="text-sm text-[#3a322a] leading-relaxed">{s.description}</p>
                  <div className="mt-7 flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-[#5C2018] opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-500">
                    Learn more <ArrowUpRight size={14} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#EBE0CE] relative overflow-hidden">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 text-center">
          <div className="section-eyebrow mb-5">Our Process</div>
          <h2 className="section-title font-display mb-16">From <span className="italic text-[#B8893C]">first sketch</span> to forever home.</h2>
          <div ref={stepsRef} className="grid grid-cols-1 md:grid-cols-4 gap-10 relative">
            {/* Connector line */}
            <div className="hidden md:block absolute top-8 left-[12%] right-[12%] h-px bg-gradient-to-r from-transparent via-[#B8893C]/50 to-transparent"></div>
            {[
              { t: "Land & Vision", d: "Site sensibility, regulatory clarity, master-planning intent.", icon: "Map" },
              { t: "Design & Craft", d: "In-house architecture, structure, MEP and interior coordination.", icon: "Compass" },
              { t: "Build & Quality", d: "Independent quality audits at every milestone, transparent reporting.", icon: "HardHat" },
              { t: "Live & Care", d: "Concierge handover and an always-on facility & lifestyle desk.", icon: "Sparkles" },
            ].map((p, i) => (
              <div key={p.t} data-reveal style={{ transitionDelay: `${i * 120}ms` }} className="reveal text-center group">
                <div className="relative mx-auto w-16 h-16 rounded-full bg-[#F5EFE6] border border-[#B8893C]/50 flex items-center justify-center text-[#5C2018] mb-5 transition-transform duration-500 group-hover:rotate-[12deg] group-hover:bg-[#5C2018] group-hover:text-[#F5EFE6]">
                  <Icon name={p.icon} size={22} />
                </div>
                <div className="font-display text-4xl text-[#B8893C] mb-2">0{i + 1}</div>
                <div className="font-display text-xl tracking-[0.05em] text-[#1F1B17] mb-2">{p.t}</div>
                <p className="text-sm text-[#3a322a]">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
